(() => {
  const normalize = value => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const footwear = new Set(['boots','loafers','sneakers','shoes','dress shoes','work shoes','clogs & mules','boat shoes','sandals','moccasins']);
  const apparel = new Set(['shorts','jeans','pants','shirts','tops','dress pants','dresses','vests','sweaters','jackets','skorts','skirts','tracksuits','leggings','coats','rompers','overalls']);
  function department(product) {
    const tags = (product.tags || []).map(normalize);
    const known = ['mens','womens','boys','girls'].filter(tag => tags.includes(tag));
    const title = normalize(product.title).replace(/[’']/g, '');
    const evidence = [['mens', /\b(mens?|men)\b/],['womens', /\b(womens?|women|ladies)\b/],['boys', /\bboys?\b/],['girls', /\bgirls?\b/]].filter(([, regex]) => regex.test(title)).map(([name]) => name);
    const all = new Set([...known, ...evidence]);
    if (all.size !== 1) return '';
    const result = [...all][0];
    if (tags.includes('kids') && ['mens','womens'].includes(result)) return '';
    if (['boys','girls'].includes(result)) {
      const age = /\b(toddler|infant|baby)\b/.test(title) ? 'young' : /\b(youth|junior)\b/.test(title) ? 'youth' : 'unspecified';
      return result + ':' + age;
    }
    return result;
  }
  function family(product) {
    const category = normalize(product.category);
    return footwear.has(category) ? 'footwear' : apparel.has(category) ? category : '';
  }
  function sizeSystem(product) {
    const label = normalize(product.size) + ' ' + normalize(product.title);
    const match = label.match(/\b(us|uk|eu|eur)\s*(?:size\s*)?\d|\d\s*(us|uk|eu|eur)\b/);
    return match ? (match[1] || match[2]) : '';
  }
  function compatible(source, candidate) {
    const size = normalize(source.size), dept = department(source), type = family(source);
    if (!size || !dept || !type || size !== normalize(candidate.size) || dept !== department(candidate) || type !== family(candidate)) return false;
    if (sizeSystem(source) !== sizeSystem(candidate)) return false;
    if (type === 'footwear') {
      const a = normalize(source.width), b = normalize(candidate.width);
      if (a && a !== b) return false;
      if (!a && b) return false;
    }
    return true;
  }
  function select(source, entries, mode, max) {
    const seen = new Set([String(source.id)]);
    return entries.filter(entry => {
      const p = entry.product;
      if (!entry.stock || !p || seen.has(String(p.id))) return false;
      seen.add(String(p.id));
      return mode === 'brand' ? normalize(source.vendor) && !['take2supply','my store'].includes(normalize(source.vendor)) && normalize(source.vendor) === normalize(p.vendor) : compatible(source, p);
    }).sort((a, b) => {
      if (mode !== 'size') return 0;
      const rank = entry => (normalize(entry.product.category) === normalize(source.category) ? 2 : 0) + (normalize(entry.product.vendor) === normalize(source.vendor) ? 1 : 0);
      return rank(b) - rank(a);
    }).slice(0, max);
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { department, family, compatible, select };
  if (typeof customElements === 'undefined' || customElements.get('t2s-related-products')) return;
  const catalogs = new Map();
  async function catalog(root, theme) {
    const key = 't2s-related-v2:' + root + theme;
    if (!catalogs.has(key)) {
      try {
        const saved = JSON.parse(sessionStorage.getItem(key));
        if (saved && Date.now() - saved.time < 300000 && Array.isArray(saved.entries)) catalogs.set(key, Promise.resolve(saved.entries));
      } catch (_) {}
    }
    if (!catalogs.has(key)) catalogs.set(key, (async () => {
      async function page(number) {
        const url = new URL(`${root}collections/all`, location.origin);
        url.searchParams.set('view', 't2s-related-data');
        url.searchParams.set('page', number);
        url.searchParams.set('preview_theme_id', theme);
        const response = await fetch(url, {credentials:'same-origin'});
        if (!response.ok) throw new Error('Related products unavailable');
        const data = await response.json();
        if (String(data.theme) !== theme || data.page !== number || !Array.isArray(data.products)) throw new Error('Invalid related product data');
        return data;
      }
      const first = await page(1), entries = [...first.products];
      if (!Number.isInteger(first.pages) || first.pages < 1 || first.total > 25000) throw new Error('Incomplete catalog');
      let count = first.count;
      for (let start = 2; start <= first.pages; start += 3) {
        const numbers = Array.from({length:Math.min(3, first.pages - start + 1)}, (_, i) => start + i);
        for (const data of await Promise.all(numbers.map(page))) {
          if (data.pages !== first.pages || data.total !== first.total) throw new Error('Catalog changed while loading');
          count += data.count;
          entries.push(...data.products);
        }
      }
      if (count !== first.total) throw new Error('Incomplete catalog');
      try { sessionStorage.setItem(key, JSON.stringify({ time: Date.now(), entries })); } catch (_) {}
      return entries;
    })());
    try { return await catalogs.get(key); } catch (error) { catalogs.delete(key); throw error; }
  }
  customElements.define('t2s-related-products', class extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) { observer.disconnect(); this.load(); }
        }, { rootMargin: '600px' });
        observer.observe(this);
      } else this.load();
    }
    async load() {
      this.dataset.state = 'loading';
      try {
        const source = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
        if (this.dataset.mode === 'size' && (!department(source) || !family(source) || !normalize(source.size))) return;
        const entries = await catalog(this.dataset.root, this.dataset.theme);
        const candidates = select(source, entries, this.dataset.mode, entries.length);
        const picks = [], max = Number(this.dataset.max);
        for (let start = 0; start < candidates.length && picks.length < max; start += max) {
          const fresh = await Promise.all(candidates.slice(start, start + max).map(async entry => {
            try {
              const url = new URL(entry.product.url, location.origin);
              if (url.origin !== location.origin) return null;
              url.searchParams.set('view', 't2s-related-card');
              url.searchParams.set('preview_theme_id', this.dataset.theme);
              const response = await fetch(url, {credentials:'same-origin', cache:'no-store'});
              if (!response.ok) return null;
              const data = await response.json();
              if (String(data.theme) !== this.dataset.theme || String(data.product?.id) !== String(entry.product.id)) return null;
              return data;
            } catch (_) { return null; }
          }));
          picks.push(...select(source, fresh.filter(Boolean), this.dataset.mode, max - picks.length));
        }
        if (!this.isConnected || !picks.length) return;
        this.querySelector('ul').innerHTML = picks.map(entry => entry.card).join('');
        this.querySelector('section').hidden = false;
        this.dataset.state = 'ready';
      } catch (error) { this.dataset.state = 'unavailable'; this.dataset.error = error.message; }
    }
  });
})();
